import { format, formatDistanceToNowStrict, parseISO } from 'date-fns'

const UNITS = ['k', 'M', 'G', 'T']

/** 1234 -> "1.2k", 0.123 -> 0.12. Mirrors the previous cockpit's number shortening. */
export function compactNumber(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–'
  for (let i = UNITS.length - 1; i >= 0; i--) {
    const threshold = 1000 ** (i + 1)
    if (Math.abs(value) >= threshold) return `${+(value / threshold).toFixed(digits)}${UNITS[i]}`
  }
  return String(+value.toFixed(digits))
}

export function percent(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–'
  return `${value.toFixed(digits)}%`
}

/** Percentages below 10 keep one decimal so small values stay readable. */
export function percentShort(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '–'
  return `${value < 10 ? value.toFixed(1) : Math.trunc(value)}%`
}

export function megabytesToGb(mb: number | null | undefined): string {
  if (mb === null || mb === undefined) return '–'
  return (mb / 1024).toFixed(2)
}

export function toDate(value: string | Date | number): Date {
  if (value instanceof Date) return value
  if (typeof value === 'number') return new Date(value)
  return parseISO(value)
}

export function dateTime(value: string | Date | null | undefined, pattern = 'yyyy-MM-dd HH:mm'): string {
  if (!value) return '–'
  try {
    return format(toDate(value), pattern)
  } catch {
    return String(value)
  }
}

export function isoDate(value: Date): string {
  return format(value, 'yyyy-MM-dd')
}

export function ago(value: string | Date | null | undefined): string {
  if (!value) return '–'
  try {
    return formatDistanceToNowStrict(toDate(value), { addSuffix: true })
  } catch {
    return String(value)
  }
}

/** "3d", "5h" or "12m" since the given instant, as the blacklist widget shows it. */
export function ageShort(value: string | Date): string {
  const ms = Date.now() - toDate(value).getTime()
  const days = Math.floor(ms / 86_400_000)
  if (days) return `${days}d`
  const hours = Math.floor(ms / 3_600_000)
  if (hours) return `${hours}h`
  return `${Math.max(0, Math.floor(ms / 60_000))}m`
}

export function stripWww(host: string): string {
  return host.startsWith('www.') ? host.slice(4) : host
}
